import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';

import type {
  LeadsPeriodPresetId,
  LeadsRegistryDateRange,
} from '@app/pages/Leads/LeadsRegistryPage/model/types';

import type { Dayjs } from 'dayjs';

export type LeadsPeriodSelectionPhase = 'idle' | 'selecting';

interface UseLeadsPeriodPickerParams {
  dates: LeadsRegistryDateRange;
  onDatesChange(range: LeadsRegistryDateRange): void;
  onDatePresetChange(id: LeadsPeriodPresetId): void;
}

// Normalizes a two-click selection into an ordered day-level range. The search
// filter formats bounds as `YYYY-MM-DD`, so anchoring to start/end of day keeps
// the request identical while guaranteeing start <= end.
const normalizeRange = (a: Dayjs, b: Dayjs): LeadsRegistryDateRange =>
  a.isAfter(b, 'day')
    ? [b.startOf('day'), a.endOf('day')]
    : [a.startOf('day'), b.endOf('day')];

export const useLeadsPeriodPicker = ({
  dates,
  onDatesChange,
  onDatePresetChange,
}: UseLeadsPeriodPickerParams) => {
  const [committedStart, committedEnd] = dates;

  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Dayjs>(() =>
    committedStart?.isValid()
      ? committedStart.startOf('month')
      : dayjs().startOf('month')
  );
  const [selectionPhase, setSelectionPhase] =
    useState<LeadsPeriodSelectionPhase>('idle');
  const [pendingStart, setPendingStart] = useState<Dayjs | null>(null);
  const [hoverDate, setHoverDate] = useState<Dayjs | null>(null);
  // Picked but not yet saved period: shown in the trigger and the calendar,
  // applied to the filters only by «Сохранить» (applying reloads the registry).
  const [draftRange, setDraftRange] = useState<LeadsRegistryDateRange | null>(
    null
  );

  const resetSelection = useCallback(() => {
    setSelectionPhase('idle');
    setPendingStart(null);
    setHoverDate(null);
    setDraftRange(null);
  }, []);

  const openChangeHandler = useCallback(
    (open: boolean) => {
      resetSelection();
      if (open && committedStart?.isValid()) {
        setViewMonth(committedStart.startOf('month'));
      }
      setIsOpen(open);
    },
    [committedStart, resetSelection]
  );

  const dayClickHandler = useCallback(
    (day: Dayjs) => {
      // First click picks a single day (saved as that day to the same day);
      // the second click extends it to a range. A third click starts over.
      if (selectionPhase === 'idle' || !pendingStart) {
        setPendingStart(day);
        setHoverDate(day);
        setDraftRange(normalizeRange(day, day));
        setSelectionPhase('selecting');
        return;
      }

      setDraftRange(normalizeRange(pendingStart, day));
      setSelectionPhase('idle');
      setPendingStart(null);
      setHoverDate(null);
    },
    [selectionPhase, pendingStart]
  );

  const saveHandler = useCallback(() => {
    if (draftRange) onDatesChange(draftRange);
    resetSelection();
    setIsOpen(false);
  }, [draftRange, onDatesChange, resetSelection]);

  const dayHoverHandler = useCallback((day: Dayjs | null) => {
    setHoverDate(day);
  }, []);

  const presetSelectHandler = useCallback(
    (id: LeadsPeriodPresetId) => {
      onDatePresetChange(id);
      resetSelection();
      setIsOpen(false);
    },
    [onDatePresetChange, resetSelection]
  );

  const resetPeriodHandler = useCallback(() => {
    // The period is optional: clearing removes it entirely rather than
    // falling back to a preset. With no dates sent, the backend defaults to
    // the last 30 days of activity.
    onDatesChange([null, null]);
    resetSelection();
    setIsOpen(false);
  }, [onDatesChange, resetSelection]);

  const preview = useMemo(() => {
    if (selectionPhase !== 'selecting' || !pendingStart) return null;

    const other = hoverDate ?? pendingStart;

    return pendingStart.isAfter(other, 'day')
      ? { start: other, end: pendingStart }
      : { start: pendingStart, end: other };
  }, [selectionPhase, pendingStart, hoverDate]);

  return {
    isOpen,
    viewMonth,
    selectionPhase,
    hasDraft: draftRange !== null,
    triggerDates: draftRange ?? dates,
    shownStart:
      draftRange?.[0] ?? (committedStart?.isValid() ? committedStart : null),
    shownEnd:
      draftRange?.[1] ?? (committedEnd?.isValid() ? committedEnd : null),
    previewStart: preview?.start ?? null,
    previewEnd: preview?.end ?? null,
    onOpenChange: openChangeHandler,
    onDayClick: dayClickHandler,
    onDayHover: dayHoverHandler,
    onViewMonthChange: setViewMonth,
    onPresetSelect: presetSelectHandler,
    onResetPeriod: resetPeriodHandler,
    onSave: saveHandler,
  };
};
